import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Switch,
  Button,
  Box,
  Typography,
} from "@mui/material";
import { useState, useEffect } from "react";
import { ArrowBack } from "@mui/icons-material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createAddon, editAddon } from "services/vehicle";
import { useToast } from "components/Toast";

const inputStyle = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#F9FAFB",
    borderRadius: "8px",
    // height: "44px",
  },
  "& .MuiInputLabel-root": {
    position: "relative",
    transform: "none",
    color: "#344054",
    fontWeight: 500,
    marginBottom: "6px",
  },
  "& .MuiInputLabel-shrink": {
    transform: "none",
  },
};

// For description field, create a separate style
const descriptionStyle = {
  ...inputStyle,
  "& .MuiOutlinedInput-root": {
    ...inputStyle["& .MuiOutlinedInput-root"],
    height: "auto",
  },
};

interface AddonsDialogProps {
  open: boolean;
  onClose: () => void;
  editData?: {
    _id: string;
    name: string;
    price: number;
    description: string;
    is_default: boolean;
  };
  onSuccess?: () => void;
}

const AddonsDialog = ({
  open,
  onClose,
  editData,
  onSuccess,
}: AddonsDialogProps) => {
  const [addonForm, setAddonForm] = useState({
    name: "",
    price: "",
    description: "",
    is_default: false,
  });

  const queryClient = useQueryClient();

  const { showToast } = useToast();

  const mutation = useMutation({
    mutationFn: (data: any) =>
      editData ? editAddon(editData._id, data) : createAddon(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["addons"] });
      showToast(
        `Add-on ${editData ? "updated" : "created"} successfully`,
        "success"
      );
      onSuccess?.();
      handleClose();
      onClose();
    },
    onError: (error: any) => {
      showToast(
        error?.response?.data?.message || "Something went wrong",
        "error"
      );
    },
  });

  useEffect(() => {
    if (editData) {
      setAddonForm({
        name: editData.name,
        price: editData.price.toString(),
        description: editData.description,
        is_default: editData.is_default,
      });
    } else {
      setAddonForm({ name: "", price: "", description: "", is_default: false });
    }
  }, [editData, open]);

  const handleChange = (field: string, value: any) => {
    setAddonForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({ ...addonForm, price: Number(addonForm.price) });
  };

  const handleClose = () => {
    setAddonForm({ name: "", price: "", description: "", is_default: false });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <ArrowBack sx={{ cursor: "pointer" }} onClick={handleClose} />
        {editData ? "Edit Add-on" : "Add Add-on"}
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
            <TextField
              label="Addon Name"
              fullWidth
              value={addonForm.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="Call Reminder"
              sx={inputStyle}
            />

            <TextField
              label="Price"
              type="number"
              fullWidth
              value={addonForm.price}
              onChange={(e) => handleChange("price", e.target.value)}
              placeholder="NGN 1,000"
              sx={inputStyle}
            />

            <TextField
              label="Description"
              multiline
              rows={4}
              fullWidth
              value={addonForm.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Describe the addon..."
              sx={descriptionStyle}
            />

            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography>Set as Default</Typography>
              <Switch
                checked={addonForm.is_default}
                onChange={(e) => handleChange("is_default", e.target.checked)}
              />
            </Box>

            <Button
              type="submit"
              variant="contained"
              sx={{ bgcolor: "#379E66", "&:hover": { bgcolor: "#2b7d4f" } }}
              disabled={mutation.isPending}
            >
              {mutation.isPending
                ? "Saving..."
                : editData
                ? "Update Add-on"
                : "Create Add-on"}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddonsDialog;
